/**
 * SparklineGrid — at-a-glance grid of mini line charts, one per well.
 *
 * Native hand-rolled SVG (no Recharts). Sparklines are too small to merit
 * the full useChartView interaction grammar — each cell is a static
 * glance-at chart. The grid surface (selector + sort + cells) is the
 * primary interaction.
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { scaleTime, scaleLinear } from 'd3-scale';
import { line as d3Line, curveMonotoneX } from 'd3-shape';
import type { AnalyticalTimeseriesResponse, ConcentrationUnit } from '@/api/dataVault';
import { WELL_COLORS } from '../../chartTheme';
import { useRegulatoryLines, type RegulatoryLine } from '../../../hooks/useRegulatoryLines';

interface Props {
  data: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
}

type SortMode = 'name' | 'latest' | 'trend';

function convertConc(val: number | null, _unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

function computeSlope(values: number[]): number {
  if (values.length < 2) return 0;
  const n = values.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += values[i];
    sumXY += i * values[i];
    sumX2 += i * i;
  }
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return 0;
  return (n * sumXY - sumX * sumY) / denom;
}

interface WellSparkData {
  wellId: string;
  wellName: string;
  latestValue: number | null;
  slope: number;
  points: Array<{ ts: number; value: number | null }>;
  colorIndex: number;
}

export function SparklineGrid({ data, unit }: Props) {
  const allAnalytes = useMemo(() => {
    const map = new Map<string, string>();
    for (const well of data.wells) {
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!map.has(code)) map.set(code, analyte.compound);
      }
    }
    return Array.from(map.entries()).map(([code, compound]) => ({ code, compound }));
  }, [data.wells]);

  const defaultAnalyte = useMemo(() => {
    const benzene = allAnalytes.find((a) => a.compound.toLowerCase() === 'benzene');
    return benzene?.code ?? allAnalytes[0]?.code ?? '';
  }, [allAnalytes]);

  const [selectedAnalyte, setSelectedAnalyte] = useState<string>(defaultAnalyte);
  const [sortMode, setSortMode] = useState<SortMode>('name');

  const wellData = useMemo(() => {
    const items: WellSparkData[] = [];
    data.wells.forEach((well, idx) => {
      const analyte = well.analytes[selectedAnalyte];
      if (!analyte) return;
      const points = analyte.measurements
        .map((m) => ({ ts: new Date(m.date).getTime(), value: convertConc(m.concentration, unit) }))
        .sort((a, b) => a.ts - b.ts);
      const numericValues = points.map((p) => p.value).filter((v): v is number => v !== null);
      const latestValue = numericValues.length > 0 ? numericValues[numericValues.length - 1] : null;
      const slope = computeSlope(numericValues);
      items.push({
        wellId: well.well_id,
        wellName: well.well_name,
        latestValue,
        slope,
        points,
        colorIndex: idx,
      });
    });
    return items;
  }, [data.wells, selectedAnalyte, unit]);

  const regVisibleCodes = useMemo(() => new Set([selectedAnalyte]), [selectedAnalyte]);
  const { lines: regulatoryLines } = useRegulatoryLines(data, regVisibleCodes);

  const sortedWells = useMemo(() => {
    const sorted = [...wellData];
    switch (sortMode) {
      case 'name':
        sorted.sort((a, b) => a.wellName.localeCompare(b.wellName));
        break;
      case 'latest':
        sorted.sort((a, b) => (b.latestValue ?? -Infinity) - (a.latestValue ?? -Infinity));
        break;
      case 'trend':
        sorted.sort((a, b) => Math.abs(b.slope) - Math.abs(a.slope));
        break;
    }
    return sorted;
  }, [wellData, sortMode]);

  if (!data.wells.length) {
    return (
      <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
        NO ANALYTICAL DATA AVAILABLE
      </div>
    );
  }

  return (
    <div role="img" aria-label="Sparkline grid overview of all well trends">
      <p className="text-xs mb-3" style={{ color: 'var(--dv-text-muted)' }}>
        Grid of mini sparkline charts — one per well — providing an at-a-glance overview of all
        wells simultaneously. Sort by name, latest value, or trend direction.
      </p>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <label className="text-xs font-medium" style={{ color: 'var(--dv-text-muted)' }}>
          Analyte
        </label>
        <select
          value={selectedAnalyte}
          onChange={(e) => setSelectedAnalyte(e.target.value)}
          className="text-xs rounded px-2 py-1 border"
          style={{
            background: 'var(--dv-bg-card)',
            borderColor: 'var(--dv-border-subtle)',
            color: 'var(--dv-text-primary)',
          }}
        >
          {allAnalytes.map((a) => (
            <option key={a.code} value={a.code}>{a.compound}</option>
          ))}
        </select>

        <label className="text-xs font-medium ml-2" style={{ color: 'var(--dv-text-muted)' }}>
          Sort
        </label>
        <select
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value as SortMode)}
          className="text-xs rounded px-2 py-1 border"
          style={{
            background: 'var(--dv-bg-card)',
            borderColor: 'var(--dv-border-subtle)',
            color: 'var(--dv-text-primary)',
          }}
        >
          <option value="name">By Name</option>
          <option value="latest">By Latest Value</option>
          <option value="trend">By Trend</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {sortedWells.map((well) => (
          <SparklineCell key={well.wellId} well={well} unit={unit} regulatoryLines={regulatoryLines} />
        ))}
      </div>
    </div>
  );
}

function TrendArrow({ slope }: { slope: number }) {
  const threshold = 0.001;
  if (slope > threshold) return <span style={{ color: '#ef4444', fontSize: 14 }}>▲</span>;
  if (slope < -threshold) return <span style={{ color: '#10b981', fontSize: 14 }}>▼</span>;
  return <span style={{ color: '#6b7280', fontSize: 14 }}>▬</span>;
}

const CELL_HEIGHT = 80;
const CELL_PADDING = 4;

function SparklineCell({
  well,
  unit,
  regulatoryLines,
}: {
  well: WellSparkData;
  unit: ConcentrationUnit;
  regulatoryLines: RegulatoryLine[];
}) {
  const color = WELL_COLORS[well.colorIndex % WELL_COLORS.length];
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(160);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const innerWidth = Math.max(0, width - CELL_PADDING * 2);
  const innerHeight = CELL_HEIGHT - CELL_PADDING * 2;

  const validPoints = well.points.filter((p) => p.value !== null) as Array<{ ts: number; value: number }>;

  const { xScale, yScale, pathD, yMaxData } = useMemo(() => {
    if (validPoints.length === 0) {
      return {
        xScale: scaleTime().domain([0, 1]).range([CELL_PADDING, CELL_PADDING + innerWidth]),
        yScale: scaleLinear().domain([0, 1]).range([CELL_PADDING + innerHeight, CELL_PADDING]),
        pathD: '',
        yMaxData: 1,
      };
    }
    const xs = validPoints.map((p) => p.ts);
    const ys = validPoints.map((p) => p.value);
    const xMin = Math.min(...xs);
    const xMax = Math.max(...xs);
    let yMax = Math.max(...ys);
    for (const line of regulatoryLines) {
      if (line.value > yMax) yMax = line.value;
    }
    const x = scaleTime()
      .domain([xMin === xMax ? xMin - 1 : xMin, xMin === xMax ? xMax + 1 : xMax])
      .range([CELL_PADDING, CELL_PADDING + innerWidth]);
    const y = scaleLinear()
      .domain([0, yMax > 0 ? yMax * 1.05 : 1])
      .range([CELL_PADDING + innerHeight, CELL_PADDING]);
    const gen = d3Line<{ ts: number; value: number }>()
      .x((d) => x(d.ts))
      .y((d) => y(d.value))
      .curve(curveMonotoneX);
    return { xScale: x, yScale: y, pathD: gen(validPoints) ?? '', yMaxData: yMax };
  }, [validPoints, innerWidth, innerHeight, regulatoryLines]);

  void xScale; // silence unused-warning — kept for future hover wiring
  void yMaxData;

  return (
    <div
      ref={containerRef}
      className="rounded-lg border p-3 transition-colors"
      style={{
        background: 'var(--dv-bg-card)',
        borderColor: 'var(--dv-border-subtle)',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--dv-accent-cyan-border)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--dv-border-subtle)';
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs truncate" style={{ color: 'var(--dv-text-primary)' }}>
          {well.wellName}
        </span>
        <TrendArrow slope={well.slope} />
      </div>

      <div className="text-sm font-mono mb-1" style={{ color: 'var(--dv-text-primary)' }}>
        {well.latestValue !== null ? well.latestValue.toFixed(3) : '—'}{' '}
        <span className="text-xs" style={{ color: 'var(--dv-text-muted)' }}>mg/L</span>
      </div>

      <svg width={width || '100%'} height={CELL_HEIGHT} style={{ display: 'block' }}>
        {/* Regulatory lines */}
        {regulatoryLines.map((line) => {
          const v = convertConc(line.value, unit);
          if (v === null) return null;
          const y = yScale(v);
          if (y < CELL_PADDING || y > CELL_PADDING + innerHeight) return null;
          const dashArray = line.style === 'dashed' ? '4 3' : line.style === 'dotted' ? '1 3' : undefined;
          return (
            <line
              key={line.key}
              x1={CELL_PADDING}
              x2={CELL_PADDING + innerWidth}
              y1={y}
              y2={y}
              stroke={line.color}
              strokeWidth={1}
              strokeDasharray={dashArray}
              opacity={0.7}
            />
          );
        })}
        {/* Sparkline */}
        {pathD && (
          <path
            d={pathD}
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}
      </svg>
    </div>
  );
}
